import { DatapointsEntityType, DatapointIdStandardEnum } from "api/ux/src";
import { InspectDataTab } from "components/modal/widget-inspect-data/types";
import { selectDimensionsBasedOnMeasures } from "datapoints/selectDimensionsBasedOnMeasures";
import { cloneDeep } from "lodash";
import { getActualsAndProjectionsAdditionalDatapoints, getAdditionalMeasuresAndTheirDimensions } from "../helper";
import { getDatapointLabelForDatapointId } from "hooks/widget-data/use-datapoints";
import { excludedDatapointsForInspectData } from "../use-inspect-data";
import { shouldEarlyReturnClickthrough, getClickthroughMeasures } from "./clickthrough-inspect-data-params";
const handleActualsAndProjections = (params) => {
    const datapoints = getActualsAndProjectionsAdditionalDatapoints();
    params.dimensions = [...datapoints.dimensions ?? []];
    params.measures = [...params.measures.filter((x) => !(x.context?.objective || x.context?.formula)), ...datapoints.measures];
};
const handleCommitmentPacingOutputs = (params) => {
    params.dimensions = [{ name: "Grouping", standardDatapoint: DatapointIdStandardEnum.PortfolioPacingAllocation }];
    params.measures = [...params.measures.filter((x) => x.context?.objective)];
};
const shouldEarlyReturnActualsAndProjections = (params, displayedLevel) => {
    if (displayedLevel === InspectDataTab.ActualsAndProjections) {
        handleActualsAndProjections(params);
        return true;
    }
    return false;
};
const shouldEarlyReturnCommitmentPacingOutputs = (params, displayedLevel) => {
    if (displayedLevel === InspectDataTab.CommitmentPacingOutputs) {
        handleCommitmentPacingOutputs(params);
        return true;
    }
    return false;
};
const getAdditionalFilteredMeasures = (params, additionalMeasures, displayedLevel) => params.measures?.filter((f) => additionalMeasures.every((aM) => aM.standardDatapoint !== f.standardDatapoint) && (displayedLevel === InspectDataTab.FullHistory ||
    f.standardDatapoint?.startsWith(displayedLevel) ||
    (displayedLevel === InspectDataTab.Asset && f.standardDatapoint?.startsWith("Instrument")) ||
    displayedLevel === InspectDataTab.Cashflows));
const buildMeasuresList = (params, additionalMeasures, entityType) => {
    const entityDimensions = entityType === DatapointsEntityType.Market || entityType === DatapointsEntityType.FundSeries
        ? []
        : params.dimensions?.filter((x) => !excludedDatapointsForInspectData.includes(x.standardDatapoint)) ?? [];
    return [
        ...additionalMeasures?.filter((am) => !params?.measures?.map((m) => m.standardDatapoint).includes(am.standardDatapoint)) ?? [],
        ...entityDimensions,
        ...params.measures
    ];
};
const hasAssetIdDimension = (dimensionForMeasures) => dimensionForMeasures?.some((dim) => dim === "Asset.AssetId") || false;
const hasInvestorIdDimension = (params) => params.dimensions?.some((dim) => dim.standardDatapoint === DatapointIdStandardEnum.FundInvestorId) || false;
const filterAllowedDimensionIds = (dimensionForMeasures, displayedLevel, entityType, hasAssetId, hasInvestorId) => (dimensionForMeasures ?? [])
    .filter((d) => !(d === "Asset.AssetId" && displayedLevel === InspectDataTab.Fund) &&
    !(d === "Fund.InvestorId" && entityType === DatapointsEntityType.Fund && !hasInvestorId))
    .filter((d) => !(hasAssetId && d === "Fund.FundId" && entityType === DatapointsEntityType.Fund));
const getDimensionForMeasures = (measures, datapointsAllowedDimensions, additionalMeasuresAndDimensions, displayedLevel, entityType, params) => {
    const dimensionForMeasures = selectDimensionsBasedOnMeasures(measures.map((a) => a?.datapoint?.standard || a?.datapoint?.custom || ""), datapointsAllowedDimensions, additionalMeasuresAndDimensions.map((x) => x.dimension.standardDatapoint));
    const hasAssetId = hasAssetIdDimension(dimensionForMeasures);
    const hasInvestorId = hasInvestorIdDimension(params);
    return filterAllowedDimensionIds(dimensionForMeasures, displayedLevel, entityType, hasAssetId, hasInvestorId);
};
const buildDimensions = (params, allowedDimensionIds, entityType) => {
    const preservedDimensions = (params.dimensions ?? []).filter((dim) => allowedDimensionIds.includes(dim.standardDatapoint || ""));
    const addedDimensions = allowedDimensionIds
        .filter((id) => !preservedDimensions.some((d) => d.standardDatapoint === id))
        .map((x) => ({
        name: entityType === DatapointsEntityType.Market ? x.replaceAll(".", "") : x,
        standardDatapoint: x
    }));
    return [...preservedDimensions, ...addedDimensions];
};
const buildNamesMeasuresForDimensions = (dimensionForMeasures, params) => dimensionForMeasures
    ?.map((x) => getDatapointLabelForDatapointId(x))
    ?.filter((x) => !params?.measures?.some((y) => y.standardDatapoint === x))
    ?.map((x) => ({
    name: `${x?.split(".")[0]}AdditionalMeasure`,
    standardDatapoint: x
}));
const buildMeasuresAndDimensions = (params, entityType, displayedLevel, datapointsAllowedDimensions, hasProjected) => {
    const measureLevels = params?.measures?.map((m) => (m.standardDatapoint ?? m.customDatapoint)?.split(".")[0]);
    const additionalMeasuresAndDimensions = getAdditionalMeasuresAndTheirDimensions(entityType, displayedLevel === InspectDataTab.FullHistory ? measureLevels : [displayedLevel]);
    const additionalMeasures = additionalMeasuresAndDimensions.map((x) => (hasProjected ? { ...x.measure, context: { projectedOnly: hasProjected } } : x.measure));
    params.measures = getAdditionalFilteredMeasures(params, additionalMeasures, displayedLevel);
    params.measures = buildMeasuresList(params, additionalMeasures, entityType);
    const measures = (params
        .measures.filter((m) => m)
        .map((m) => datapointsAllowedDimensions.find((d) => d.datapoint?.standard === m.standardDatapoint && d.datapoint?.custom === m.customDatapoint)));
    return { additionalMeasuresAndDimensions, measures };
};
const updateDimensionsAndPrependMeasures = (params, entityType, displayedLevel, datapointsAllowedDimensions, measures, additionalMeasuresAndDimensions) => {
    if (entityType !== DatapointsEntityType.Market && entityType !== DatapointsEntityType.FundSeries) {
        const allowedDimensionIds = getDimensionForMeasures(measures, datapointsAllowedDimensions, additionalMeasuresAndDimensions, displayedLevel, entityType, params);
        params.dimensions = buildDimensions(params, allowedDimensionIds, entityType);
        const dimensionForMeasures = selectDimensionsBasedOnMeasures(measures.map((a) => a?.datapoint?.standard || a?.datapoint?.custom || ""), datapointsAllowedDimensions, additionalMeasuresAndDimensions.map((x) => x.dimension.standardDatapoint));
        const namesMeasuresForDimensions = buildNamesMeasuresForDimensions(dimensionForMeasures, params) ?? [];
        params.measures.unshift(...namesMeasuresForDimensions);
    }
};
const ensurePeriodsIfNotProjected = (params, hasProjected) => {
    if (!hasProjected) {
        params.measures = params.measures?.map((x) => ({ ...x, periods: x?.periods ?? 1 }));
    }
};
const shouldReturnNullForSingleLabel = (widgetParamsNew, displayedLevel) => widgetParamsNew[0]?.measures.length === 1 &&
    (widgetParamsNew[0]?.measures[0].standardDatapoint === DatapointIdStandardEnum.FundFundName ||
        widgetParamsNew[0]?.measures[0].standardDatapoint === DatapointIdStandardEnum.AssetAssetName) &&
    displayedLevel !== InspectDataTab.FullHistory;
const processSingleParam = ({ params, displayedLevel, entityType, timePeriod, datapointsAllowedDimensions, clickthroughDatapoints, hasProjected, startDate, endDate }) => {
    params.startDate = startDate;
    params.endDate = endDate;
    if (shouldEarlyReturnClickthrough(params, displayedLevel, entityType, timePeriod, clickthroughDatapoints)) {
        return;
    }
    if (shouldEarlyReturnActualsAndProjections(params, displayedLevel)) {
        return;
    }
    if (shouldEarlyReturnCommitmentPacingOutputs(params, displayedLevel)) {
        return;
    }
    const { measures, additionalMeasuresAndDimensions } = buildMeasuresAndDimensions(params, entityType, displayedLevel, datapointsAllowedDimensions, hasProjected);
    updateDimensionsAndPrependMeasures(params, entityType, displayedLevel, datapointsAllowedDimensions, measures, additionalMeasuresAndDimensions);
    params.measures.push(...getClickthroughMeasures(params, clickthroughDatapoints, entityType));
    ensurePeriodsIfNotProjected(params, hasProjected);
};
export const processDisplayedLevelParams = ({ displayedLevel, widgetParams, entityType, timePeriod, datapointsAllowedDimensions, clickthroughDatapoints, hasProjected, startDate, endDate }) => {
    const widgetParamsNew = cloneDeep(widgetParams);
    widgetParamsNew.forEach((params) => {
        processSingleParam({
            clickthroughDatapoints,
            datapointsAllowedDimensions,
            displayedLevel,
            endDate,
            entityType,
            hasProjected,
            params,
            startDate,
            timePeriod
        });
    });
    if (shouldReturnNullForSingleLabel(widgetParamsNew, displayedLevel)) {
        return null;
    }
    return widgetParamsNew;
};
